import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "api";
import useNav from "core/hooks/useNav";

import { useAuth } from "features/auth/fabric";
import { useStart } from "features/start/fabric";


export const query = {
	get: (profileId, ssk, cid) => ({
		queryKey: ["content", ssk || cid],
		queryFn: () => {
			let body = { profileId };
			if (ssk) body.ssk = ssk;
			else if (cid) body.contentId = Number(cid);
			return api.content.get(body);
		},
		enabled: Boolean(ssk || cid)
	}),
	gift: (sgk, device) => ({
		queryKey: ["gift", sgk],
		queryFn: () => api.content.gift.get({ sgk, device }),
		enabled: (Boolean(sgk) && Boolean(device))
	}),
	giftById: (profileId, giftId) => ({
		queryKey: ["gift-bought", giftId],
		queryFn: () => api.content.gift.getById(giftId, { profileId }),
		enabled: Boolean(giftId)
	})
};

export const useQueryUnlock = () => {
	const start = useStart();
	const nav = useNav();

	const ssk = nav.keys.ssk;
	const cid = nav.keys.cid;

	const content = useQuery(query.get(start.profile.id, ssk, cid));
	const isPass = Boolean(content.data?.pass);
	const isPassValid = Boolean(content.data?.time);

	return { ...content, isPass, isPassValid };
};

export const useMutateUnlock = () => {
	const auth = useAuth();
	const start = useStart();
	const nav = useNav();

	const ssk = nav.keys.ssk;
	const cid = nav.keys.cid;

	return useMutation({
		mutationFn: () => {
			let body = { profileId: start.profile.id };
			if (ssk) body.ssk = ssk;
			else if (cid) {
				body.contentId = Number(cid);
				body.device = auth.device;
			};
			return api.content.unlock(body);
		},
		onSuccess: (data) => nav.open(data.url)
	});
};

// GIFT
export const useQueryGift = () => {
	const start = useStart();
	const nav = useNav();
	const gid = nav.keys.gid;
	const cid = nav.keys.cid;

	return useQuery(gid ? query.giftById(start.profile?.id, gid) : query.get(start.profile?.id, null, cid));
};

export const useQueryRedeem = () => {
	const auth = useAuth();
	const nav = useNav();
	const sgk = nav.keys.sgk;

	return useQuery(({
		queryKey: ["gift", sgk],
		queryFn: () => api.content.gift.get({ sgk, device: auth.device }),
		enabled: (Boolean(sgk) && Boolean(auth.device))
	}));
};

export const useMutateBuyGift = () => {
	const start = useStart();
	const { device } = useAuth();

	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (contentId) => api.content.gift.buy({ profileId: start.profile.id, contentId, device }),
		onSuccess: async ({ url, id }) => {
			sessionStorage.setItem("gift-id", id);
			await navigator.share({ url });
			queryClient.invalidateQueries({ queryKey: ["start"] });
			queryClient.invalidateQueries({ queryKey: ["user-history"] });
		}
	});
};

export const useMutateUnlockGift = () => {
	const auth = useAuth();
	const nav = useNav();
	const queryClient = useQueryClient();
	const sgk = nav.keys.sgk;

	return useMutation({
		mutationFn: () => api.content.gift.unlock({ sgk, device: auth.device }),
		onSuccess: (data, share) => {
			if (share) nav.open(data.url);
			else {
				queryClient.invalidateQueries({ queryKey: ["start"] });
				queryClient.invalidateQueries({ queryKey: ["gift"] });
				queryClient.invalidateQueries({ queryKey: ["gift-bought"] });
			}
		}
	});
};